import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Store, Hotel, Building2, TrendingUp, Newspaper, Handshake } from "lucide-react";
import { PARTNERS } from "@/data/content";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Partenaires() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

 // Étape 6. Quatre voies de partenariat premium
 const pathways = [
 {
 icon: Store,
 label: l("Distribution", "Distribution", "Distribuição"),
 title: l("Devenir distributeur.", "Become a distributor.", "Torne-se distribuidor."),
 body: l(
 "Épiceries fines, boutiques spécialisées, e-commerce premium, nous confions nos marques à des distributeurs qui partagent notre exigence.",
 "Fine grocers, specialised shops, premium e-commerce, we entrust our brands to distributors who share our standards.",
 "Mercearias finas, lojas especializadas, e-commerce premium, confiamos as nossas marcas a distribuidores que partilham a nossa exigência."
 ),
 cta: l("Demander notre catalogue distributeur", "Request our distributor catalogue", "Solicitar o catálogo de distribuição"),
 subject: "Distribution",
 benefits: [
 l("Tarifs professionnels", "Professional pricing", "Preços profissionais"),
 l("Exclusivités territoriales", "Territorial exclusivities", "Exclusividades territoriais"),
 l("Support marketing dédié", "Dedicated marketing support", "Apoio de marketing dedicado"),
 ],
 },
 {
 icon: Hotel,
 label: l("Retail & Hôtellerie", "Retail & Hospitality", "Retalho & Hotelaria"),
 title: l("Nos produits en boutique et en hôtel.", "Our products in shops and hotels.", "Os nossos produtos em lojas e hotéis."),
 body: l(
 "Concept stores, palaces, resorts, spas d'exception, nos collections NANDA SÁ Cosmétiques, NANDA SÁ et JOHN TĀDO trouvent naturellement leur place dans les adresses raffinées.",
 "Concept stores, palaces, resorts, exceptional spas, our NANDA SÁ Cosmétiques, NANDA SÁ and JOHN TĀDO collections find their natural place in refined venues.",
 "Concept stores, palácios, resorts, spas de excepção, as nossas colecções NANDA SÁ Cosmétiques, NANDA SÁ e JOHN TĀDO encontram naturalmente o seu lugar em endereços refinados."
 ),
 cta: l("Prendre rendez-vous commercial", "Book a commercial appointment", "Marcar reunião comercial"),
 subject: "Retail",
 benefits: [
 l("Amenities sur mesure", "Bespoke amenities", "Amenities à medida"),
 l("Coffrets personnalisés", "Personalised gift sets", "Coffrets personalizados"),
 l("Formations produit", "Product training", "Formação de produto"),
 ],
 },
 {
 icon: Building2,
 label: l("Institutionnel", "Institutional", "Institucional"),
 title: l("Coopération & marchés publics.", "Cooperation & public procurement.", "Cooperação & concursos públicos."),
 body: l(
 "Administrations, ONG, bailleurs, agences de développement, nous répondons aux appels d'offres liés à l'énergie solaire, à la formation et à la valorisation des filières.",
 "Administrations, NGOs, funders, development agencies, we respond to tenders related to solar energy, training and value-chain valorisation.",
 "Administrações, ONG, financiadores, agências de desenvolvimento, respondemos aos concursos ligados à energia solar, à formação e à valorização das fileiras."
 ),
 cta: l("Écrire à la direction", "Write to management", "Escrever à direcção"),
 subject: "Institutionnel",
 benefits: [
 l("Références vérifiables", "Verifiable references", "Referências verificáveis"),
 l("Équipes locales certifiées", "Certified local teams", "Equipas locais certificadas"),
 l("Reporting normalisé", "Standardised reporting", "Reporting normalizado"),
 ],
 },
 {
 icon: TrendingUp,
 label: l("Investisseurs", "Investors", "Investidores"),
 title: l("Investir dans une vision.", "Invest in a vision.", "Investir numa visão."),
 body: l(
 "Fonds d'impact, family offices, investisseurs institutionnels, nous ouvrons des tours de table ciblés pour accélérer l'industrialisation et l'expansion internationale.",
 "Impact funds, family offices, institutional investors, we open targeted funding rounds to accelerate industrialisation and international expansion.",
 "Fundos de impacto, family offices, investidores institucionais, abrimos rondas de financiamento direccionadas para acelerar a industrialização e a expansão internacional."
 ),
 cta: l("Recevoir le dossier confidentiel", "Receive the confidential deck", "Receber o dossier confidencial"),
 subject: "Investissement",
 benefits: [
 l("Modèle diversifié", "Diversified model", "Modelo diversificado"),
 l("Marché ouest-africain en expansion", "Growing West African market", "Mercado oeste-africano em expansão"),
 l("Impact ESG intégré", "Integrated ESG impact", "Impacto ESG integrado"),
 ],
 },
 ];

 const hasPartners = PARTNERS && PARTNERS.length > 0;
 const groups = hasPartners
 ? PARTNERS.reduce((acc, p) => {
 (acc[p.category] = acc[p.category] || []).push(p);
 return acc;
 }, {})
 : {};
 const categories = Object.keys(groups);

 return (
 <div data-testid="page-partenaires">
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <Breadcrumbs items={[{ label: l("Partenaires", "Partners", "Parceiros") }]} className="mb-6" />
 <div className="label-overline text-nanda-muted">{l("Partenaires", "Partners", "Parceiros")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("Construisons ensemble.", "Let us build together.", "Construamos juntos.")}
 </h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "Distribution, retail, hôtellerie, institutions, investissement, presse, quatre voies pour collaborer avec NANDA SÁ. Nos partenariats sont pensés pour durer.",
 "Distribution, retail, hospitality, institutions, investment, press, four ways to collaborate with NANDA SÁ. Our partnerships are designed to last.",
 "Distribuição, retalho, hotelaria, instituições, investimento, imprensa, quatro vias para colaborar com a NANDA SÁ. As nossas parcerias são pensadas para durar."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* 4 VOIES DE PARTENARIAT. Cœur de la conversion B2B */}
 <section className="border-b border-nanda-line" data-testid="partnership-pathways">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <FadeUp className="mb-16 md:mb-20 max-w-3xl">
 <div className="label-overline text-nanda-muted mb-4">{l("Quatre voies", "Four pathways", "Quatro vias")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l("Comment collaborer avec NANDA SÁ ?", "How to collaborate with NANDA SÁ?", "Como colaborar com a NANDA SÁ?")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {pathways.map((p, i) => {
 const Icon = p.icon;
 return (
 <StaggerItem key={p.subject} className="bg-nanda-bg p-10 md:p-14 flex flex-col" data-testid={`pathway-${p.subject.toLowerCase}`}>
 <div className="flex items-start justify-between mb-8">
 <div className="text-nanda-ink">
 <Icon size={32} strokeWidth={1.2} />
 </div>
 <div className="label-overline text-nanda-muted num-editorial">0{i + 1}</div>
 </div>
 <div className="label-overline text-nanda-terracotta mb-3">{p.label}</div>
 <h3 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-6">{p.title}</h3>
 <div className="rule-gold w-24 mb-8" />
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed mb-8">{p.body}</p>
 <ul className="space-y-3 mb-10">
 {p.benefits.map((b, k) => (
 <li key={k} className="flex items-baseline gap-3 text-nanda-ink text-[15px]">
 <span className="h-px w-6 bg-nanda-terracotta" />
 <span>{b}</span>
 </li>
 ))}
 </ul>
 <Link
 to={`/contact?subject=${encodeURIComponent(p.subject)}`}
 data-testid={`pathway-cta-${p.subject.toLowerCase}`}
 className="btn-arrow mt-auto inline-flex items-center gap-3 label-overline text-nanda-ink border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500 self-start"
 >
 {p.cta} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </StaggerItem>
 );
 })}
 </Stagger>
 </div>
 </section>

 {/* Presse en bonus */}
 <section className="border-b border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
 <FadeUp className="md:col-span-5">
 <Newspaper size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-6" />
 <div className="label-overline text-nanda-muted mb-4">{l("Presse", "Press", "Imprensa")}</div>
 <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight">
 {l("Journalistes & rédactions.", "Journalists & newsrooms.", "Jornalistas & redacções.")}
 </h3>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-7">
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed mb-8 max-w-2xl">
 {l(
 "Interviews, portraits, visites de sites, kits presse, notre équipe communication vous répond dans un délai de 48 heures. Accès sur demande à notre médiathèque officielle.",
 "Interviews, portraits, site visits, press kits, our communications team responds within 48 hours. On-request access to our official media library.",
 "Entrevistas, retratos, visitas a locais, kits de imprensa, a nossa equipa de comunicação responde no prazo de 48 horas. Acesso a pedido à nossa mediateca oficial."
 )}
 </p>
 <div className="flex flex-wrap gap-4">
 <Link
 to="/mediatheque"
 data-testid="partnership-media-link"
 className="btn-arrow inline-flex items-center gap-3 label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500"
 >
 {l("Voir la médiathèque", "See the media library", "Ver a mediateca")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 <Link
 to="/contact?subject=Presse"
 data-testid="partnership-press-cta"
 className="btn-arrow inline-flex items-center gap-3 label-overline border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500"
 >
 {l("Écrire à la presse", "Write to press", "Contacto de imprensa")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Partenaires officiels (si présents) */}
 {hasPartners && categories.map((cat, ci) => (
 <section key={cat} className={`border-b border-nanda-line ${ci % 2 === 1 ? "bg-nanda-bgAlt" : ""}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">0{ci + 1}</div>
 <h2 className="font-serif text-4xl md:text-5xl mt-4">{cat}</h2>
 </FadeUp>
 <Stagger className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {groups[cat].map((p, i) => (
 <StaggerItem key={i} className="bg-nanda-bg p-8 md:p-10" data-testid={`partner-${cat}-${i}`}>
 <div className="font-serif text-2xl md:text-3xl">{p.name}</div>
 <div className="label-overline text-nanda-muted mt-3">{p.region}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 ))}

 {/* CTA final : Handshake */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <Handshake size={28} strokeWidth={1.2} className="text-nanda-ink mb-6" />
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05] text-nanda-ink">
 {l("Un projet en tête ? Parlons-en.", "A project in mind? Let's talk.", "Um projecto em mente? Falemos.")}
 </h2>
 <p className="mt-8 max-w-xl text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {l(
 "Notre équipe étudie chaque demande sous 48 heures. Toutes les collaborations sérieuses commencent par une conversation.",
 "Our team reviews every request within 48 hours. All serious collaborations begin with a conversation.",
 "A nossa equipa analisa cada pedido em 48 horas. Todas as colaborações sérias começam com uma conversa."
 )}
 </p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex md:justify-end items-end">
 <Link
 to="/contact"
 data-testid="partnership-final-cta"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 {l("Écrire à NANDA SÁ", "Write to NANDA SÁ", "Escrever à NANDA SÁ")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
